import React, { useState, useEffect } from 'react'
import { ButtonElement, useAngularServices } from '@/react/components'
import closeIcon from '@/assets/icons/close-icon.svg'
import { css } from '@emotion/react'
import { useRef } from 'react'
import ReminderItem from './modal/ReminderItem'
import { CurrentUserType } from '../types/documentation'
import { Reminder } from '../types/training'

interface Position {
  top: number
  left: number
}

interface ConfirmationReminderModalProps {
  title: string
  handleClick: (reminderValues: {
    reminder_1: number
    reminder_2: number
    reminder_3: number
    reminder_repeat: boolean
  }) => void
  handleClose: () => void
  currentReminder: Reminder
}

const ConfirmationReminderModal = ({
  title = '',
  handleClick,
  handleClose,
  currentReminder,
}: ConfirmationReminderModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color
  const [isButtonDisabled, setButtonDisabled] = useState(false)
  const initialValues = {
    reminder_1: currentReminder?.reminder_1 || 0,
    reminder_2: currentReminder?.reminder_2 || 0,
    reminder_3: currentReminder?.reminder_3 || 0,
    reminder_repeat: currentReminder?.reminder_repeat || false,
  }
  const [reminderValues, setReminderValues] = useState(initialValues)
  const [hasChanges, setHasChanges] = useState(false)

  const baseStyle = getBaseStyle(variableColor)

  const handleOutsideClick = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const checkForChanges = (newValues: typeof initialValues) => {
    const hasValueChanged = Object.keys(newValues).some(
      (key) =>
        newValues[key as keyof typeof newValues] !==
        initialValues[key as keyof typeof newValues],
    )
    setHasChanges(hasValueChanged)
  }

  const truncatedTitle =
    title.length > 80 ? `${title.substring(0, 80)}...` : title

  const reminders = ['First Reminder:', 'Second Reminder:', 'Third Reminder:']

  return (
    <div
      css={baseStyle}
      className="modal__wrapper"
      ref={modalRef}
      onMouseOver={(e) => {
        e.stopPropagation()
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <img
        src={closeIcon}
        alt="cross-icon"
        className="close__icon"
        onClick={() => {
          handleClose()
        }}
      />
      <div className="modal__info">
        <div className="message__title">
          <p className="message__name">{truncatedTitle}</p>
          <p className="message__name">Email Reminders</p>
        </div>
        <div className="reminder__container">
          {reminders.map((name, index) => (
            <ReminderItem
              key={index}
              name={name}
              value={
                reminderValues[
                  `reminder_${index + 1}` as keyof typeof reminderValues
                ] as number
              }
              onChange={(value) => {
                const newValues = {
                  ...reminderValues,
                  [`reminder_${index + 1}`]: value,
                }
                setReminderValues(newValues)
                checkForChanges(newValues)
              }}
            />
          ))}
        </div>
        <div className="checkbox__container">
          <input
            type="checkbox"
            checked={reminderValues.reminder_repeat}
            onChange={(e) => {
              const newValues = {
                ...reminderValues,
                reminder_repeat: e.target.checked,
              }
              setReminderValues(newValues)
              checkForChanges(newValues)
            }}
          />
          <p>Send Daily Reminders Until Completed</p>
        </div>
        <ButtonElement
          text="Save"
          buttonType="submit"
          functionToTrigger={() => {
            setButtonDisabled(true)
            handleClick(reminderValues)
            handleClose()
          }}
          disabled={!hasChanges || isButtonDisabled}
        />
      </div>
    </div>
  )
}

const getBaseStyle = (variableColor: string) =>
  css({
    position: 'absolute',
    backgroundColor: 'white',
    boxShadow: '0px 7px 29px 0px rgba(0, 0, 0, 0.18)',
    right: '85px',
    top: '0',
    zIndex: 10000,
    transform: 'translateZ(0)',
    isolation: 'isolate',
    '.modal__info': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      width: '290px',
    },
    '.close__icon': {
      position: 'absolute',
      top: 10,
      right: 10,
      width: '10px',
      height: '10px',
      cursor: 'pointer',
    },
    '.message__title': {
      marginBottom: '10px',
    },
    '.message__name': {
      display: 'flex',
      justifyContent: 'center',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      textAlign: 'center',
      fontSize: 12,
      color: '#3E3D3D',
      fontWeight: 600,
      margin: 0,
    },
    '.reminder__container': {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      marginBottom: '15px',
    },
    '.checkbox__container': {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '26px',
      'input[type="checkbox"]': {
        width: '22px',
        height: '22px',
        border: '1px solid #C6C6C6',
        appearance: 'none',
        backgroundColor: 'white',
        cursor: 'pointer',
        outline: 'none',
        margin: 0,
        '&:checked': {
          backgroundColor: variableColor,
          border: `1px solid ${variableColor}`,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            left: '6px',
            top: '3px',
            width: '8px',
            height: '12px',
            border: 'solid white',
            borderWidth: '0 2px 2px 0',
            transform: 'rotate(45deg)',
          },
        },
      },
      p: {
        color: '#575757',
        fontSize: '12px',
        margin: 0,
      },
    },
    '.delete__title': {
      marginBottom: '14px',
    },
    '.delete__subtitle': {
      color: '#666',
      textAlign: 'center',
      fontSize: '14px',
      margin: 0,
      marginBottom: '25px',
      width: '206px',
    },
    '.message__email': {
      margin: 0,
      fontWeight: 600,
    },
  })

export default ConfirmationReminderModal
